import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { HitsterEvent } from '../types';
import './PrintStartYears.css';

const API_BASE = process.env.REACT_APP_API_URL || '';

const PrintStartYears: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  
  const [event, setEvent] = useState<HitsterEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'per-round' | 'all-years'>('per-round');

  useEffect(() => {
    const loadEvent = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/events/${eventId}`);
        if (response.ok) {
          const data = await response.json();
          setEvent(data);
        }
      } catch (err) {
        console.error('Failed to load event:', err);
      }
      setLoading(false);
    };
    loadEvent();
  }, [eventId]);

  const handlePrint = () => {
    window.print();
  };

  const handleBack = () => {
    navigate('/events');
  };

  const printableRounds = useMemo(() => {
    if (!event) return [];

    const seenIds = new Set<string>();

    // Gebruik de array-volgorde (zoals in RoundScoring), niet de order-property,
    // zodat de rondenummering overeenkomt met hoe de speler ze heeft ingesteld.
    return [...event.rounds]
      .filter(round => {
        const normalizedName = (round.name || '').toLowerCase();
        return !normalizedName.includes('bingo');
      })
      .filter(round => {
        if (seenIds.has(round.id)) return false;
        seenIds.add(round.id);
        return true;
      });
  }, [event]);

  // Zorg dat elke ronde een uniek startjaar heeft (stabiel via useMemo)
  // Moet vóór early returns staan vanwege React Hooks regels
  const roundsWithYears = useMemo(() => {
    const sorted = printableRounds;
    const usedYears = new Set<number>(
      sorted.filter(r => r.startYear).map(r => r.startYear!)
    );
    return sorted.map(round => {
      if (round.startYear) return { ...round, effectiveStartYear: round.startYear };
      let year: number;
      do {
        year = Math.floor(Math.random() * (2020 - 1950 + 1)) + 1950;
      } while (usedYears.has(year));
      usedYears.add(year);
      return { ...round, effectiveStartYear: year };
    });
  }, [printableRounds]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return <div className="print-start-years loading">Laden...</div>;
  }

  if (!event) {
    return <div className="print-start-years error">Evenement niet gevonden</div>;
  }

  const sortedRounds = printableRounds;

  return (
    <div className="print-start-years">
      <div className="print-years-controls no-print">
        <button onClick={handleBack} className="print-years-close-btn" title="Terug" aria-label="Terug">
          ×
        </button>
        <h1>Startjaargetallen - {event.name}</h1>
        <div className="print-years-view-mode-toggle no-print">
          <button 
            className={`print-years-mode-btn ${viewMode === 'per-round' ? 'active' : ''}`}
            onClick={() => setViewMode('per-round')}
            title="Per ronde kaartjes"
          >
            📋 Per ronde
          </button>
          <button 
            className={`print-years-mode-btn ${viewMode === 'all-years' ? 'active' : ''}`}
            onClick={() => setViewMode('all-years')}
            title="Alle jaren als set"
          >
            📄 Alle jaren
          </button>
        </div>
        <button onClick={handlePrint} className="print-years-print-btn">
          🖨️ Printen
        </button>
      </div>

      <div className="years-content">
        {viewMode === 'per-round' ? (
          <>
            <div className="years-header">
              <h2>{event.name}</h2>
              <p>Startjaargetallen per ronde</p>
            </div>

            <div className="years-grid">
              {roundsWithYears.map((round, index) => (
                <div key={round.id} className="year-card">
                  <div className="round-name">{`Ronde ${index + 1}`}</div>
                  <div className="year-value">{round.effectiveStartYear}</div>
                </div>
              ))}
            </div>

            {sortedRounds.length === 0 && (
              <div className="empty-message">Geen rondes gevonden</div>
            )}
          </>
        ) : (
          <>
            <div className="years-header">
              <h2>{event.name}</h2>
              <p>Alle startjaargetallen als set</p>
            </div>

            <div className="all-years-set">
              <div className="all-years-cards-grid">
                {roundsWithYears.map((round, index) => (
                  <div key={round.id} className="all-years-card">
                    <div className="all-years-card-content">
                      <div className="all-years-card-year">{round.effectiveStartYear}</div>
                      <div className="all-years-card-subtitle">{`Ronde ${index + 1}`}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {sortedRounds.length === 0 && (
              <div className="empty-message">Geen rondes gevonden</div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PrintStartYears;
